/********************************
 *** Custom Utility Functions
 *******************************/
import { DateTime } from 'luxon'

// MySQL database formats
const sqlDateFormat = 'yyyy-LL-dd'
const sqlDateTimeFormat = 'yyyy-LL-dd HH:mm:ss'

// Vue formats
const defaultDateFormat = 'LL/dd/yyyy'
const defaultTimeWithSecondsFormat = 'h:mm:ss a'
const defaultTimeWithoutSecondsFormat = 'h:mm a'
const defaultTimeZoneFormat = 'ZZZZ'

const dateTimeFormatWithSeconds = defaultDateFormat + ' ' + defaultTimeWithSecondsFormat
const dateTimeFormatWithSecondsAndTimeZone = defaultDateFormat + ' ' + defaultTimeWithSecondsFormat + ' ' + defaultTimeZoneFormat
const dateTimeFormatWithoutSeconds = defaultDateFormat + ' ' + defaultTimeWithoutSecondsFormat
const dateTimeFormatWithTimeZoneWithoutSeconds = defaultDateFormat + ' ' + defaultTimeWithoutSecondsFormat + ' ' + defaultTimeZoneFormat

const FormattingUtilities = {

  /**
   * Hide zeros
   */
  hideZeros(x) {
    if (x === 0) {
      return ""
    } else {
      return x
    }
  },

  /**
   * Number with commas
   */
  numberWithCommas(x) {
    if (x === 0) {
      // Allow 0 to return 0
      return 0
    } else if (x) {
      return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }
  },

  /**
   * Format boolean
   *
   * Takes a boolean and converts it to either 'Yes' or 'No', if conversionType is undefined or set to 1
   * Takes a boolean and converts it to either 'True' or 'False', if conversionType is set to 2
   * Takes a boolean and converts it to either '1' or '0', if conversionType is set to 3
   * Takes a boolean and converts it to either 'Active' or 'Inactive', if conversionType is set to 4
   * If the value is undefined or null, no formatting is performed
   * Example: true is converted to 'Yes'
   * Example: false is converted to 'No'
   * @param {boolean|undefined|null} value
   * @param {number} [conversionType]
   * @returns {string|undefined|null}
   */
  formatBoolean(value, conversionType) {
    if (value === undefined || value === null) {
      return value
    } else {
      switch (conversionType) {
        case 1:
        default:
          return (value == true) ? 'Yes' : 'No'
        case 2:
          return (value == true) ? 'True' : 'False'
        case 3:
          return (value == true) ? '1' : '0'
        case 4:
          return (value == true) ? 'Active' : 'Inactive'
      }
    }
  },

  /**
   * @see formatBoolean
   * @param {boolean|undefined|null} value
   * @returns {string|undefined|null}
   */
  formatBooleanYesOrNo(value) {
    return FormattingUtilities.formatBoolean(value, 1)
  },

  /**
   * @see formatBoolean
   * @param {boolean|undefined|null} value
   * @returns {string|undefined|null}
   */
  formatBooleanTrueOrFalse(value) {
    return FormattingUtilities.formatBoolean(value, 2)
  },

  /**
   * @see formatBoolean
   * @param {boolean|undefined|null} value
   * @returns {string|undefined|null}
   */
  formatBooleanOneOrZero(value) {
    return FormattingUtilities.formatBoolean(value, 3)
  },

  /**
   * @see formatBoolean
   * @param {boolean|undefined|null} value
   * @returns {string|undefined|null}
   */
  formatBooleanActiveOrInactive(value) {
    return FormattingUtilities.formatBoolean(value, 4)
  },

  /**
   * Format percentage
   *
   * Takes a string and converts it to a percentage string
   * If the value is undefined or null, no formatting is performed
   * Example: 0.56126 is converted 56.13%
   * @param {string|undefined|null} value
   * @returns {string|undefined|null}
   */
  formatPercentage(value) {
    if (value === undefined || value === null) {
      return value
    } else {
      return Intl.NumberFormat('en-US', {
        style: 'percent',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value)
    }
  },

  /**
   * Format phone number
   *
   * Takes a string and converts it to a formatted phone number string
   * If the value is undefined or null, no formatting is performed
   * @param {string|undefined|null} value
   * @returns {string|undefined|null}
   */
  formatPhoneNumber(value) {
    const cleaned = ('' + value).replace(/\D/g, '')
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)
    if (match) {
      return '(' + match[1] + ')' + match[2] + '-' + match[3]
    }
    return null
  },

  /**
   * Format query params
   *
   * take an object of params, formatting sub-objects as key/value arrays which helps with query string serialization
   */
  formatQueryParams(params) {
    params = JSON.parse(JSON.stringify(params)) // get a full deep copy of the passed object
    if (params.filters) this.flattenSubObjectToKeyValueArray(params, 'filters')

    // remove unnecessary params
    if (!params.page || params.page === 1) delete params.page
    if (!params.limit) delete params.limit
    if (!params.sort_by) delete params.sort_by
    if (!params.sort_scope) delete params.sort_scope
    if (!params.sort_dir) delete params.sort_dir
    if (!params.with) delete params.with
    if (!params.appends) delete params.appends
    if (!params.select) delete params.select
    if (!params.distinct) delete params.distinct
    if (!params.setCsvMap) delete params.setCsvMap

    return params
  },

  /**
   * @param {Object} params
   * @return {Object}
   */
  simpleFormatQueryParams(params) {
    params = JSON.parse(JSON.stringify(params)) // get a full deep copy of the passed object
    if (params.filters) this.flattenSubObjectToKeyValueArray(params, 'filters')
    if (Array.isArray(params.order_by) && params.order_by.length) {
      params.order_by = encodeURIComponent(JSON.stringify(params.order_by))
    } else {
      delete params.order_by
    }

    // remove unnecessary params
    if (!params.page || params.page === 1) delete params.page

    return params
  },

  /**
   * Flatten sub-object to K-V array
   *
   * for a sub-object at object->key, flatten the sub-object into array keys on the original object
   */
  flattenSubObjectToKeyValueArray(object, key) {
    const properties = Object.keys(object[key])
    properties.forEach(property => {
      const value = object[key][property]
      if (value !== undefined) {
        object[`${key}[${property}]`] = value
      }
    })

    delete object[key]
  },

  /**
   * @return {string}
   */
  getDateTimeFormatWithTimeZoneWithoutSeconds() {
    return dateTimeFormatWithTimeZoneWithoutSeconds
  },

  /**
   * Format dateTime from SQL
   *
   * Takes a datetime string in an SQL format, which is assumed to be UTC, and converts it into a format in the local timezone
   * Example: 2022-02-10 14:55:07 (timezone assumed to be UTC) is converted to Feb 10, 2022 9:55 AM EST (timezone America/New York)
   *
   * @param {string} value - datetime value (UTC)
   * @param {string|undefined} [format] - @see https://moment.github.io/luxon/#/formatting?id=table-of-tokens
   * @param {string|Zone|'UTC'} [timeZoneOverride] - a zone identifier. As a string, that can be any IANA zone supported
   * by the host environment, or a fixed-offset name of the form 'UTC+3', or the strings 'local' or 'utc'.
   * You may also supply an instance of a DateTime#Zone class.
   * @param {boolean|undefined} [returnInvalidExplain] - if set to true an explanation is returned when datetime is invalid.
   * If not set or set to false an empty string is returned when datetime is invalid.
   * @returns {string}
   */
  formatDateTimeFromSQL(value, format = dateTimeFormatWithoutSeconds, timeZoneOverride = 'UTC', returnInvalidExplain = false) {
    const dt = DateTime.fromSQL(value, { zone: timeZoneOverride })
    if (dt.isValid) {
      return timeZoneOverride
        ? dt.setZone(timeZoneOverride).toFormat(format)
        : dt.setZone(DateTime.local().zoneName).toFormat(format)
    } else {
      return returnInvalidExplain
        ? dt.invalidReason + ': ' + dt.invalidExplanation
        : ''
    }
  },

  /**
   * Format dateTime from SQL with seconds
   *
   * @see formatDateTimeFromSQL
   */
  formatDateTimeFromSQLWithSeconds(value, format = dateTimeFormatWithSeconds, timeZoneOverride = undefined, returnInvalidExplain = false) {
    return FormattingUtilities.formatDateTimeFromSQL(value, format, timeZoneOverride, returnInvalidExplain)
  },

  /**
   * Format dateTime from SQL without seconds
   *
   * @see formatDateTimeFromSQL
   */
  formatDateTimeFromSQLWithoutSeconds(value, format = dateTimeFormatWithoutSeconds, timeZoneOverride = undefined, returnInvalidExplain = false) {
    return FormattingUtilities.formatDateTimeFromSQL(value, format, timeZoneOverride, returnInvalidExplain)
  },

  /**
   * Format dateTime from SQL to Pacific Time with seconds
   * @see formatDateTimeFromSQL
   */
  formatDateTimeFromSQLToPacificWithSeconds(value, format = dateTimeFormatWithSeconds, timeZoneOverride = 'America/Los_Angeles', returnInvalidExplain = false) {
    return FormattingUtilities.formatDateTimeFromSQL(value, format, timeZoneOverride, returnInvalidExplain)
  },

  /**
   * Format dateTime from SQL to Pacific Time without seconds
   * @see formatDateTimeFromSQL
   */
  formatDateTimeFromSQLToPacific(value, format = dateTimeFormatWithoutSeconds, timeZoneOverride = 'America/Los_Angeles', returnInvalidExplain = false) {
    return FormattingUtilities.formatDateTimeFromSQL(value, format, timeZoneOverride, returnInvalidExplain)
  },

  /**
   * Format date by truncating the hours, minutes, and seconds from a dataTime from SQL to Pacific Time. In other words,
   * given the dateTime SQL value, adjust for Pacific Time, then return only the date portion.
   * @see formatDateTimeFromSQL
   */
  formatDateByTruncationOfDateTimeFromSQLToPacific(value, format = defaultDateFormat, timeZoneOverride = 'America/Los_Angeles', returnInvalidExplain = false) {
    return FormattingUtilities.formatDateTimeFromSQL(value, format, timeZoneOverride, returnInvalidExplain)
  },

  /**
   * Format dateTime from ISO
   *
   * Takes a datetime string in an ISO format and converts it into a format in the local timezone
   * Example: 2022-05-14T04:39:51.000000Z is converted to 05/14/2022 12:39 AM EDT
   *
   * @param {string} value - datetime value
   * @param {string|undefined} [format] - @see https://moment.github.io/luxon/#/formatting?id=table-of-tokens
   * @param {string|Zone|undefined} [timeZoneOverride] - a zone identifier. As a string, that can be any IANA zone supported
   * by the host environment, or a fixed-offset name of the form 'UTC+3', or the strings 'local' or 'utc'.
   * You may also supply an instance of a DateTime#Zone class.
   * @param {boolean|undefined} [returnInvalidExplain] - if set to true an explanation is returned when datetime is invalid.
   * If not set or set to false an empty string is returned when datetime is invalid.
   * @returns {string}
   */
  formatDateTimeFromISO(value, format = dateTimeFormatWithoutSeconds, timeZoneOverride = undefined, returnInvalidExplain = false) {
    const dt = DateTime.fromISO(value)
    if (dt.isValid) {
      return timeZoneOverride
        ? dt.setZone(timeZoneOverride).toFormat(format)
        : dt.setZone(DateTime.local().zoneName).toFormat(format)
    }
    return returnInvalidExplain
      ? dt.invalidReason + ': ' + dt.invalidExplanation
      : ''
  },

  /**
   * Format dateTime from SQL to Pacific Time without seconds
   * @see formatDateTimeFromISO
   */
  formatDateTimeFromISOToPacific(value, format = dateTimeFormatWithoutSeconds, timeZoneOverride = 'America/Los_Angeles', returnInvalidExplain = false) {
    return FormattingUtilities.formatDateTimeFromISO(value, format, timeZoneOverride, returnInvalidExplain)
  },

  /**
   * Format Date from SQL
   *
   * Takes a date string in an SQL format and converts it into a local format
   * Example: 2022-02-10 is converted to Feb 10, 2022
   *
   * @param {string} value - date value
   * @param {string|undefined} [format] - @see https://moment.github.io/luxon/#/formatting?id=table-of-tokens
   * @param {boolean|undefined} [returnInvalidExplain] - if set to true an explanation is returned when date is invalid.
   * If not set or set to false an empty string is returned when date is invalid.
   * @returns {string}
   */
  formatDateFromSQL(value, format = defaultDateFormat, returnInvalidExplain = false) {
    const dt = DateTime.fromSQL(value)
    if (dt.isValid) {
      return dt.toFormat(format)
    }
    return returnInvalidExplain
      ? dt.invalidReason + ': ' + dt.invalidExplanation
      : ''
  },

  /**
   * Format Date from ISO
   *
   * Takes a date string in an ISO format and converts it into a local format
   * Example: 2022-05-14T02:44:52.000000Z is converted to 05/13/2022
   *
   * @param {string} value - date value
   * @param {string|undefined} [format] - @see https://moment.github.io/luxon/#/formatting?id=table-of-tokens
   * @param {boolean|undefined} [returnInvalidExplain] - if set to true an explanation is returned when date is invalid.
   * If not set or set to false an empty string is returned when date is invalid.
   * @returns {string}
   */
  formatDateFromISO(value, format = defaultDateFormat, returnInvalidExplain = false) {
    const dt = DateTime.fromISO(value)
    if (dt.isValid) {
      return dt.toFormat(format)
    }
    return returnInvalidExplain ? dt.invalidReason + ': ' + dt.invalidExplanation : ''
  },

  /**
   * Format Date to SQL
   * Takes a javascript Date object and converts it to a datetime string in an SQL format, which is assumed to be UTC
   *
   * @param {Date} jsDate
   * @returns {string}
   */
  formatDateToSQL(jsDate) {
    const dt = DateTime.fromJSDate(jsDate)
    return dt.setZone('UTC').toFormat(sqlDateTimeFormat)
  },

  /**
   * Format Date to specified format
   * Takes a javascript Date object and converts it to a specified format
   *
   * @param {Date} jsDate
   * @param {string} format
   * @returns {string}
   */
  formatDate(jsDate, format) {
    const dt = DateTime.fromJSDate(jsDate)
    return dt.toFormat(format)
  },

  /**
   * This is function necessary for a few reasons:
   * 1) Some date pickers are set so that the user doesn't need to choose hours, minutes, or seconds, therefore
   * when parsed to a DateTime format these values all resolve to zero's (start of the day)
   * 2) When considering a date range, "to" dates should have 1 day added, to represent the end of day of the day that is chosen
   * 3) The javascript Date object must be converted to UTC, as this is what the server expects
   * 4) If for some reason, the date that is passed cannot be parsed into a valid date, undefined is returned
   *
   * Note: Deprecated in favor of primeVueCalendarToSQL
   *
   * @param {Date} jsDate
   * @param {boolean|undefined} [endOfDayAdjustment]
   * @returns {string|undefined}
   * @deprecated
   */
  adjustDateForRequest(jsDate, endOfDayAdjustment = false) {
    const timeZone = 'UTC'
    const format = 'yyyy-LL-dd HH:mm:ss'
    let adjusted = DateTime.fromJSDate(jsDate)
    adjusted = adjusted.isValid
      ? endOfDayAdjustment === false
        ? adjusted.setZone(timeZone).toFormat(format)
        : adjusted.plus({ day: 1 }).setZone(timeZone).toFormat(format)
      : undefined
    return adjusted
  },

  /**
   * This is function necessary for a few reasons:
   * 1) Some date pickers are set so that the user doesn't need to choose hours, minutes, or seconds, therefore
   * when parsed to a DateTime format these values all resolve to zero's (start of the day)
   * 2) When considering a date range, "to" dates should have 1 day added, to represent the end of day of the day that is chosen.
   * 3) The javascript Date object must be converted to UTC, as this is what the server expects
   * 4) If for some reason, the date that is passed cannot be parsed into a valid date, undefined is returned
   *
   * Most often this function will be used adjust dates within a Vue Calendar component
   * and adjusting it into string that will be in UTC time, ready for storing in the database.
   *
   * @param {Date} jsDate
   * @param {Object} [options]
   * @param {boolean} [options.includeTime=true] - formatted result includes time when true, otherwise does not include time
   * @param {boolean} [options.endOfDayAdjustment=false] - adjustment 1 day forward, ignored when includeTime false
   * @param {string} [options.timeZoneAdjustment=undefined] - adjustment to "place" user in this timezone, ignored when includeTime false
   * @returns {string|undefined}
   */
  primeVueCalendarAdjuster(jsDate, options = {
    includeTime: true,
    endOfDayAdjustment: false,
    timeZoneAdjustment: undefined,
  }) {
    const targetTimeZone = 'UTC'
    const targetFormat = options.includeTime ? 'yyyy-LL-dd HH:mm:ss' : 'yyyy-LL-dd'
    const local = DateTime.now()
    let adjusted = DateTime.fromJSDate(jsDate)
    let otherTimeZone, timeZoneOffset = undefined

    if (options.includeTime && options.timeZoneAdjustment) {
      otherTimeZone = DateTime.now().setZone(options.timeZoneAdjustment)
      timeZoneOffset = local.offset - otherTimeZone.offset
    }

    if (adjusted.isValid) {
      if (options.includeTime) {
        if (options.endOfDayAdjustment) {
          adjusted = adjusted.plus({ day: 1 }).minus({ second: 1 })
        }
        if (options.timeZoneAdjustment) {
          adjusted = adjusted.plus({ minute: timeZoneOffset })
        }
        adjusted = adjusted.setZone(targetTimeZone)
      }
      return adjusted.toFormat(targetFormat)
    } else {
      return undefined
    }
  },

  /**
   * Parse value to SQL Date
   *
   * @param {string} value
   * @param {string[]|string|undefined} [parseToken]
   * @see https://moment.github.io/luxon/#/parsing?id=table-of-tokens
   * @returns {string|false}
   */
  parseValueToSQLDate(value, parseToken) {
    const defaultTokens = ['M/d/yyyy', 'yyyy-M-d', 'MMM d, yyyy', 'MMMM d, yyyy']
    let successfulParse = false
    let parsed
    // examples of default accepted date formats '5/9/2022', '2022-05-09', 'Aug 5, 2022', 'August 5, 2022'
    if (parseToken === undefined) {
      parseToken = defaultTokens
    }
    if (Array.isArray(parseToken)) {
      parseToken.every((token) => {
        parsed = DateTime.fromFormat(value, token)
        if (parsed.isValid) {
          successfulParse = true
          return false // break out of "every" loop after the first valid parse
        }
        return true
      })
    } else {
      parsed = DateTime.fromFormat(value, parseToken)
      successfulParse = parsed.isValid
    }
    return (successfulParse) ? parsed.setZone('UTC').toFormat(sqlDateFormat) : false
  },

  /**
   * Parse value to SQL dateTime
   *
   * @param {string} value
   * @param {string[]|string|undefined} [parseToken]
   * @see https://moment.github.io/luxon/#/parsing?id=table-of-tokens
   * @returns {string|false}
   */
  parseValueToSQLDateTime(value, parseToken) {
    const dateTokens = ['M/d/yyyy', 'yyyy-M-d', 'MMM d, yyyy', 'MMMM d, yyyy']
    // examples of default accepted date formats '5/9/2022', '2022-05-09', 'Aug 5, 2022', 'August 5, 2022'
    const timeTokens = ['h:mm:ss a', 'hh:mm:ss a', 'h:mm a', 'hh:mm a']
    // examples of default accepted time formats '4:23:10 PM', '04:23:10 PM', '4:23 PM', '04:23 PM'
    // note: currently, only 12-hour format is accepted, so meridiem is required, however it is case-insensitive.
    // note: in the absence of seconds, parse may still be valid, but will be set to 00
    let successfulParse = false
    let defaultTokens = []
    let parsed
    if (parseToken === undefined) {
      dateTokens.forEach((d) => {
        timeTokens.forEach((t) => {
          defaultTokens.push(d + ' ' + t) // include every combination of accepted dateTokens and timeTokens
        })
      })
      parseToken = defaultTokens
    }
    if (Array.isArray(parseToken)) {
      parseToken.every((token) => {
        parsed = DateTime.fromFormat(value, token)
        if (parsed.isValid) {
          successfulParse = true
          return false // break out of "every" loop after the first valid parse
        }
        return true
      })
    } else {
      parsed = DateTime.fromFormat(value, parseToken)
      successfulParse = parsed.isValid
    }
    return (successfulParse) ? parsed.setZone('UTC').toFormat(sqlDateTimeFormat) : false
  },

  /**
   * Convert dateTime from SQL to Date
   * Takes a datetime string in an SQL format, which is assumed to be UTC, and returns a Date object of equal value
   *
   * @param {string} value - datetime value (UTC)
   * @returns {Date}
   */
  convertDateTimeFromSQLToDate(value) {
    return new Date(DateTime.fromSQL(value, { zone: 'UTC' }).valueOf())
  },

  /**
   * Convert date from String to Date
   * Takes a date string, which is assumed to be UTC, and returns a Date object of equal value
   *
   * @param {string} value - date value (UTC)
   * @returns {Date}
   */
  convertDateFromStringToDate(value) {
    return new Date(DateTime.fromSQL(value).valueOf())
  },

  /**
   * Convert date from a datetime string to Date object.
   * Takes a datetime string, which is assumed to be UTC, and returns a Date object with respect to the
   * a specific time zone. Most often this function will be used adjust dates coming from the database
   * and adjusting it into an object that is consumed by a Vue Calendar component.
   *
   * @param {string} date
   * @param {string} timeZone
   * @return {undefined|Date}
   */
  convertUTCDateToJSDateWithRespectToTimeZone(date, timeZone) {
    if (!!date && !!timeZone) {
      const local = DateTime.now()
      const otherTimeZone = DateTime.fromSQL(date, { zone: 'UTC' }).setZone(timeZone)
      return otherTimeZone.plus({ minute: otherTimeZone.offset - local.offset }).toJSDate()
    } else {
      return undefined
    }
  },

  /**
   * @param {string|null} date
   * @param {string} format
   * @returns {DateTime}
   * @throws {Error}
   */
  getLuxonDateTimeFromString(date, format) {
    if (typeof date !== 'string') {
      throw new Error('date parameter is not a string')
    }
    switch (format) {
      case 'sql':
        return DateTime.fromSQL(date, { zone: 'UTC' })
      case 'iso':
        return DateTime.fromISO(date)
      default:
        throw new Error('format parameter is invalid')
    }
  },

  /**
   * Calculates the duration between two timestamp values and returns the result in the format hh:mm:ss
   *
   * @param {string|null} startDate
   * @param {string} startDateFormat
   * @param {string|null} endDate
   * @param {string} endDateFormat
   * @returns {string}
   */
  formatDuration(startDate, startDateFormat, endDate, endDateFormat) {
    if (startDate === null || endDate === null) {
      return ''
    }
    try {
      const start = this.getLuxonDateTimeFromString(startDate, startDateFormat)
      const end = this.getLuxonDateTimeFromString(endDate, endDateFormat)
      const durationObject = start.isValid && end.isValid
        ? end.diff(start, ['hours', 'minutes', 'seconds']).toObject()
        : undefined
      if (durationObject) {
        const hours = durationObject.hours >= 10 ? durationObject.hours : '0' + durationObject.hours
        const minutes = durationObject.minutes >= 10 ? durationObject.minutes : '0' + durationObject.minutes
        const seconds = durationObject.seconds >= 10 ? durationObject.seconds : '0' + durationObject.seconds
        return [hours, minutes, seconds].join(':')
      } else {
        return ''
      }
    } catch (e) {
      console.error(e)
    }
  },


  /**
   * @param {Object} woy
   * @param {string} joinOn
   * @param {boolean} prependSingleDigitWeeksWithZero
   * @return {string}
   */
  concatenateWeekOfYear(woy, joinOn = '-', prependSingleDigitWeeksWithZero = true) {
    if (prependSingleDigitWeeksWithZero) {
      return [woy.year, parseInt(woy.week) < 10 ? '0' + woy.week : woy.week].join(joinOn)
    } else {
      return [woy.year, woy.week].join(joinOn)
    }
  },

}

export default FormattingUtilities
